<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const extensions = ref([]);
const isLoading = ref(false);
const deletingId = ref(null);
const isCreating = ref(false);
const isSavingId = ref(null);
const showCreateForm = ref(false);
const selectedUserId = ref('');
const customNumber = ref('');
const editingId = ref(null);
const editNumber = ref('');

const accountId = computed(() => route.params.accountId);
const agents = computed(() => store.getters['agents/getAgents'] || []);
const extensionUserIds = computed(
  () => new Set(extensions.value.map(e => e.user.id))
);
const availableAgents = computed(() =>
  agents.value.filter(a => !extensionUserIds.value.has(a.id))
);

const apiBase = () =>
  `/api/v1/accounts/${accountId.value}/inboxes/${props.inbox.id}/telephony/extensions`;

async function fetchExtensions() {
  isLoading.value = true;
  try {
    const { data } = await axios.get(apiBase());
    extensions.value = data;
  } catch {
    useAlert(t('INBOX_MGMT.TELEPHONY.EXTENSIONS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
}

async function createExtension() {
  if (!selectedUserId.value) return;
  isCreating.value = true;
  try {
    const payload = { user_id: selectedUserId.value };
    if (customNumber.value) payload.number = customNumber.value;
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId.value}/inboxes/${props.inbox.id}/telephony/extension`,
      payload
    );
    extensions.value.push(data);
    showCreateForm.value = false;
    selectedUserId.value = '';
    customNumber.value = '';
    useAlert(t('INBOX_MGMT.TELEPHONY.EXTENSIONS.CREATE_SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.TELEPHONY.EXTENSIONS.CREATE_ERROR'));
  } finally {
    isCreating.value = false;
  }
}

async function removeExtension(ext) {
  if (
    // eslint-disable-next-line no-restricted-globals
    !confirm(
      t('INBOX_MGMT.TELEPHONY.EXTENSIONS.CONFIRM_DELETE', {
        number: ext.number,
        name: ext.user.name,
      })
    )
  )
    return;

  deletingId.value = ext.id;
  try {
    await axios.delete(`${apiBase()}/${ext.id}`);
    extensions.value = extensions.value.filter(e => e.id !== ext.id);
    useAlert(t('INBOX_MGMT.TELEPHONY.EXTENSIONS.DELETE_SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.TELEPHONY.EXTENSIONS.DELETE_ERROR'));
  } finally {
    deletingId.value = null;
  }
}

function startEdit(ext) {
  editingId.value = ext.id;
  editNumber.value = ext.number;
}

function cancelEdit() {
  editingId.value = null;
  editNumber.value = '';
}

async function saveEdit(ext) {
  if (!editNumber.value) return;
  isSavingId.value = ext.id;
  try {
    const { data } = await axios.patch(`${apiBase()}/${ext.id}`, {
      number: editNumber.value,
    });
    const idx = extensions.value.findIndex(e => e.id === ext.id);
    if (idx !== -1) extensions.value[idx] = data;
    cancelEdit();
    useAlert(t('INBOX_MGMT.TELEPHONY.EXTENSIONS.UPDATE_SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.TELEPHONY.EXTENSIONS.UPDATE_ERROR'));
  } finally {
    isSavingId.value = null;
  }
}

onMounted(fetchExtensions);
</script>

<template>
  <div class="flex flex-col gap-4 p-6">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ t('INBOX_MGMT.TELEPHONY.EXTENSIONS.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-10 mt-0.5">
          {{ t('INBOX_MGMT.TELEPHONY.EXTENSIONS.DESC') }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <NextButton
          icon="i-lucide-refresh-cw"
          variant="ghost"
          size="sm"
          :is-loading="isLoading"
          @click="fetchExtensions"
        />
        <NextButton
          icon="i-lucide-plus"
          size="sm"
          :label="t('INBOX_MGMT.TELEPHONY.EXTENSIONS.CREATE_BUTTON')"
          @click="showCreateForm = !showCreateForm"
        />
      </div>
    </div>

    <div
      v-if="showCreateForm"
      class="rounded-xl border border-n-weak p-4 flex flex-col gap-3"
    >
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.TELEPHONY.EXTENSIONS.CREATE_TITLE') }}
      </p>
      <div class="flex gap-3 items-end">
        <div class="flex-1">
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            {{ t('INBOX_MGMT.TELEPHONY.EXTENSIONS.SELECT_AGENT') }}
          </label>
          <select
            v-model="selectedUserId"
            class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-blue-8"
          >
            <option value="" disabled>
              {{
                t('INBOX_MGMT.TELEPHONY.EXTENSIONS.SELECT_AGENT_PLACEHOLDER')
              }}
            </option>
            <option
              v-for="agent in availableAgents"
              :key="agent.id"
              :value="agent.id"
            >
              {{ agent.name }}
            </option>
          </select>
        </div>
        <div class="w-32">
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            {{ t('INBOX_MGMT.TELEPHONY.EXTENSIONS.CUSTOM_NUMBER_LABEL') }}
          </label>
          <input
            v-model="customNumber"
            type="number"
            class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-blue-8"
            :placeholder="
              t('INBOX_MGMT.TELEPHONY.EXTENSIONS.CUSTOM_NUMBER_PLACEHOLDER')
            "
          />
        </div>
        <NextButton
          size="sm"
          :label="t('INBOX_MGMT.TELEPHONY.EXTENSIONS.CREATE_BUTTON')"
          :is-loading="isCreating"
          :disabled="!selectedUserId"
          @click="createExtension"
        />
        <NextButton
          size="sm"
          variant="ghost"
          icon="i-lucide-x"
          @click="
            showCreateForm = false;
            selectedUserId = '';
            customNumber = '';
          "
        />
      </div>
    </div>

    <div v-if="isLoading" class="flex justify-center py-10">
      <SpinnerLoader />
    </div>

    <div
      v-else-if="extensions.length === 0"
      class="rounded-xl border border-n-weak p-8 text-center text-sm text-n-slate-10"
    >
      {{ t('INBOX_MGMT.TELEPHONY.EXTENSIONS.EMPTY') }}
    </div>

    <div v-else class="rounded-xl border border-n-weak overflow-hidden">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-n-weak bg-n-alpha-1">
            <th class="text-left px-4 py-3 font-medium text-n-slate-11">
              {{ t('INBOX_MGMT.TELEPHONY.EXTENSIONS.COL_AGENT') }}
            </th>
            <th class="text-left px-4 py-3 font-medium text-n-slate-11">
              {{ t('INBOX_MGMT.TELEPHONY.EXTENSIONS.COL_NUMBER') }}
            </th>
            <th class="text-left px-4 py-3 font-medium text-n-slate-11">
              {{ t('INBOX_MGMT.TELEPHONY.EXTENSIONS.COL_STATUS') }}
            </th>
            <th class="px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="ext in extensions"
            :key="ext.id"
            class="border-b border-n-weak last:border-0 hover:bg-n-alpha-1 transition-colors"
          >
            <td class="px-4 py-3">
              <span class="font-medium text-n-slate-12">{{
                ext.user.name
              }}</span>
            </td>
            <td class="px-4 py-3">
              <div
                v-if="editingId === ext.id"
                class="flex items-center gap-1.5"
              >
                <input
                  v-model="editNumber"
                  type="number"
                  class="w-20 rounded border border-n-weak bg-n-background px-2 py-0.5 text-sm font-mono text-n-slate-12 outline-none focus:border-n-blue-8"
                  @keyup.enter="saveEdit(ext)"
                  @keyup.escape="cancelEdit"
                />
                <NextButton
                  icon="i-lucide-check"
                  size="xs"
                  :is-loading="isSavingId === ext.id"
                  @click="saveEdit(ext)"
                />
                <NextButton
                  icon="i-lucide-x"
                  size="xs"
                  variant="ghost"
                  @click="cancelEdit"
                />
              </div>
              <div v-else class="flex items-center gap-1.5 group">
                <span
                  class="font-mono text-n-slate-12 bg-n-alpha-2 px-2 py-0.5 rounded"
                >
                  {{ ext.number }}
                </span>
                <button
                  class="opacity-0 group-hover:opacity-100 transition-opacity text-n-slate-9 hover:text-n-slate-12"
                  @click="startEdit(ext)"
                >
                  <span class="i-lucide-pencil size-3.5" />
                </button>
              </div>
            </td>
            <td class="px-4 py-3">
              <span
                class="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full"
                :class="
                  ext.status === 'active'
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-amber-3 text-n-amber-11'
                "
              >
                <span
                  class="size-1.5 rounded-full"
                  :class="
                    ext.status === 'active' ? 'bg-n-teal-9' : 'bg-n-amber-9'
                  "
                />
                {{
                  ext.status === 'active'
                    ? t('INBOX_MGMT.TELEPHONY.EXTENSIONS.STATUS_ACTIVE')
                    : t('INBOX_MGMT.TELEPHONY.EXTENSIONS.STATUS_SUSPENDED')
                }}
              </span>
            </td>
            <td class="px-4 py-3 text-right">
              <NextButton
                icon="i-lucide-trash-2"
                variant="ghost"
                size="sm"
                color-scheme="alert"
                :is-loading="deletingId === ext.id"
                @click="removeExtension(ext)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p class="text-xs text-n-slate-9">
      {{
        t('INBOX_MGMT.TELEPHONY.EXTENSIONS.RANGE_HINT', {
          base: Number(accountId) * 100,
        })
      }}
    </p>
  </div>
</template>
